/**
 * `Surfaces` — **F19 / M17**: the registry, the view specs and the backends, on one anchored page.
 *
 * ⚠️ **Freshness is the load-bearing thing on this screen.** A surface is read by an agent that
 * cannot tell whether the answer is current, so `as_of` is shown as a fact and the `fresh`/`stale`
 * verdict is shown as a **provisional judgement about that fact** — with the window and its known
 * failure mode stated, never a bare green tick.
 *
 * ⚠️ **The residency posture is per surface, and it is the posture AT PUBLICATION.** A Vectorize
 * surface says, in words, that we cannot show its vectors stayed in the tenant's region and that the
 * exposure was recorded on their audit trail. Rendering that as a small grey caveat would be the
 * quiet version of a decision that was taken openly.
 */
import {
  Badge,
  Callout,
  Card,
  DataTable,
  EmptyState,
  HelpText,
  PageHeader,
  Skeleton,
  StatusPill,
} from '@dedupr/ui';
import { ErrorState, useQuery } from '@dedupr/app-kit';
import { authorized, readJson } from '../planeFetch.js';
import './Surfaces.css';

interface Surface {
  id: string;
  kind: 'view' | 'graph' | 'embedding';
  view_id: string;
  version: number;
  backend: string;
  location: string;
  rows: number;
  as_of: string;
  state: string;
  freshness: { state: 'fresh' | 'stale'; age_hours: number; sla_hours: number; note: string } | null;
  residency: { region: string; region_pinned: boolean; statement: string };
}

interface Registry {
  surfaces: Surface[];
  serving: { view_id: string; versions: number[] }[];
  sla_note: string;
}

const BACKEND_LABEL: Record<string, string> = {
  r2_artifact: 'Dedupr object store (your region)',
  vectorize: 'Cloudflare Vectorize',
  byo_postgres: 'your Postgres',
  byo_vectorize: 'your vector store',
};

export function Surfaces({ orgId }: { orgId?: string }) {
  const query = useQuery<Registry | null>(async () => {
    if (!orgId) return null;
    return readJson<Registry>(await authorized(`/v1/surfaces?org_id=${encodeURIComponent(orgId)}`), 'The surface registry');
  }, [orgId]);

  if (!orgId) {
    return (
      <EmptyState
        title="No organisation in scope"
        description="A surface publishes one organisation's cleaned data. Sign in to one to see its registry."
      />
    );
  }
  if (query.status === 'loading') return <Skeleton />;
  if (query.status === 'error') {
    return <ErrorState code={query.errorCode} message={query.errorMessage} onRetry={query.reload} />;
  }
  const data = query.data;
  if (!data) return <Skeleton />;

  /* ⚠️ Any unpinned surface raises the notice once, at the top — rather than only inside a row
     somebody may never expand. It is a standing property of what has been published, not a detail. */
  const unpinned = data.surfaces.filter((s) => !s.residency.region_pinned);

  return (
    <div className="surfaces">
      <PageHeader
        title="AI-ready surfaces"
        description="What Dedupr publishes for your agents to read — and how current each one is."
      />

      {unpinned.length > 0 ? (
        <Callout tone="warning" title="Some of these are outside the residency guarantee, and it was recorded">
          {unpinned.length} surface{unpinned.length === 1 ? '' : 's'} {unpinned.length === 1 ? 'is' : 'are'} published to
          a store we cannot show is pinned to your region. That is a deliberate, recorded exception rather
          than an oversight: every publish writes the region it was for onto your own audit trail, so a
          later audit can see exactly what was placed there. See the detail on each row below.
        </Callout>
      ) : null}

      <Card headingLevel={2} title="Published surfaces">
        <HelpText>{data.sla_note}</HelpText>
        <DataTable
          label="Published surfaces"
          rows={data.surfaces}
          rowKey={(row) => row.id}
          empty="Nothing is published yet. A surface is built from decided state — resolve some matches first, then publish a view."
          columns={[
            {
              id: 'view',
              header: 'Surface',
              kind: 'primary',
              width: 1.8,
              cell: (row) => `${row.view_id}@v${row.version} · ${row.kind}`,
            },
            { id: 'backend', header: 'Published to', width: 1.8, cell: (row) => BACKEND_LABEL[row.backend] ?? row.backend },
            { id: 'rows', header: 'Rows', width: 1, cell: (row) => `${row.rows.toLocaleString()} rows` },
            {
              id: 'fresh',
              header: 'Freshness',
              width: 1.6,
              /* ⚠️ The AGE, not just the verdict. A "fresh" badge on its own invites exactly the
                 misreading the provisional window makes possible. */
              cell: (row) =>
                row.freshness === null ? (
                  <span className="surfaces__muted">retired</span>
                ) : (
                  <StatusPill
                    tone={row.freshness.state === 'fresh' ? 'success' : 'warning'}
                    description={`This surface was built ${row.freshness.age_hours} hours ago`}
                  >
                    {row.freshness.state} · {row.freshness.age_hours}h
                  </StatusPill>
                ),
            },
            {
              id: 'residency',
              header: 'Region',
              width: 1.4,
              cell: (row) =>
                row.residency.region_pinned ? (
                  <Badge variant="neutral">{row.residency.region.toUpperCase()}</Badge>
                ) : (
                  <Badge variant="warning">not pinned</Badge>
                ),
            },
          ]}
          expanded={(row) => (
            <div className="surfaces__detail">
              <p>
                Built <strong>{new Date(row.as_of).toLocaleString()}</strong> · at <code>{row.location}</code>
              </p>
              {row.freshness ? <HelpText>{row.freshness.note}</HelpText> : null}
              {/* ⚠️ The residency sentence in full, in the row, in words. Not a tooltip. */}
              <Callout tone={row.residency.region_pinned ? 'info' : 'warning'} title="Where this lives">
                {row.residency.statement}
              </Callout>
            </div>
          )}
        />
      </Card>

      <div id="views">
        <Card headingLevel={2} title="Versions still serving">
          <HelpText>
            A new version builds alongside the old one, and retirement is a separate act. An agent
            querying an older version keeps getting it until somebody retires it deliberately.
          </HelpText>
          <DataTable
            label="Versions serving per view"
            rows={data.serving}
            rowKey={(row) => row.view_id}
            empty="No view has been published yet."
            columns={[
              { id: 'view', header: 'View', kind: 'primary', width: 2, cell: (row) => row.view_id },
              {
                id: 'versions',
                header: 'Serving',
                width: 3,
                cell: (row) => row.versions.map((v) => `v${v}`).join(', '),
              },
            ]}
          />
        </Card>
      </div>

      <div id="backends">
        <Card headingLevel={2} title="Backends">
          <HelpText>
            Two backends publish today and two refuse by name. Publishing to your own Postgres or vector
            store needs a connection a Worker cannot open to an arbitrary database at runtime — the same
            constraint that shaped our own data plane. The registry and the projection are ready for it;
            the transport is not, and the API says so rather than failing at the moment it matters.
          </HelpText>
        </Card>
      </div>
    </div>
  );
}
